import React, { useState, useEffect, useRef } from 'react';
import { VscAdd, VscEdit, VscTrash, VscCheck, VscClose } from 'react-icons/vsc';
import { getDiagrams, createDiagram, renameDiagram, deleteDiagram } from '../../api/ganttDiagrams';

function PlanningSidebar({ project, activeTabId, onOpenDiagram, role }) {
  const [diagrams, setDiagrams] = useState([]);
  const [editId,   setEditId]   = useState(null);
  const [editName, setEditName] = useState('');
  const [creating, setCreating] = useState(false);
  const [newName,  setNewName]  = useState('');
  const newInputRef  = useRef(null);
  const editInputRef = useRef(null);

  const canEdit = ['admin','engineer','manager','foreman'].includes(role);

  useEffect(() => {
    if (!project?._id) return;
    getDiagrams(project._id).then(r => setDiagrams(r.data)).catch(() => {});
  }, [project?._id]);

  useEffect(() => { if (creating) newInputRef.current?.focus(); }, [creating]);
  useEffect(() => { if (editId)   editInputRef.current?.focus(); }, [editId]);

  const handleCreate = async () => {
    const name = newName.trim() || 'New Plan';
    try {
      const res = await createDiagram({ project: project._id, name });
      setDiagrams(prev => [...prev, res.data]);
      setCreating(false); setNewName('');
      onOpenDiagram(res.data);
    } catch {}
  };

  const handleRename = async (id) => {
    const name = editName.trim();
    if (!name) { setEditId(null); return; }
    try {
      const res = await renameDiagram(id, name);
      setDiagrams(prev => prev.map(d => d._id === id ? res.data : d));
    } catch {}
    setEditId(null);
  };

  const handleDelete = async (id) => {
    if (!confirm('Delete this diagram and all its tasks?')) return;
    try {
      await deleteDiagram(id);
      setDiagrams(prev => prev.filter(d => d._id !== id));
    } catch {}
  };

  const startEdit = (d) => { setEditId(d._id); setEditName(d.name); };

  return (
    <div className="sp-nav">
      {diagrams.map(d => (
        <div key={d._id} className="sp-project-row">
          {editId === d._id ? (
            <>
              <input
                ref={editInputRef}
                className="sp-diagram-rename"
                value={editName}
                onChange={e => setEditName(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleRename(d._id); if (e.key === 'Escape') setEditId(null); }}
              />
              <button className="btn-icon" onClick={() => handleRename(d._id)}><VscCheck size={13} /></button>
              <button className="btn-icon" onClick={() => setEditId(null)}><VscClose size={13} /></button>
            </>
          ) : (
            <>
              <button
                className={`sp-nav-item${activeTabId === `gantt-${d._id}` ? ' active' : ''}`}
                style={{ flex: 1 }}
                onClick={() => onOpenDiagram(d)}
              >
                {d.name}
              </button>
              {canEdit && (
                <>
                  <button className="btn-icon sp-diagram-btn" title="Rename" onClick={() => startEdit(d)}><VscEdit size={12} /></button>
                  <button className="btn-icon sp-diagram-btn" title="Delete" onClick={() => handleDelete(d._id)}><VscTrash size={12} /></button>
                </>
              )}
            </>
          )}
        </div>
      ))}

      {creating ? (
        <div className="sp-diagram-new-row">
          <input
            ref={newInputRef}
            className="sp-diagram-rename"
            value={newName}
            placeholder="Diagram name…"
            onChange={e => setNewName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') handleCreate(); if (e.key === 'Escape') { setCreating(false); setNewName(''); } }}
          />
          <button className="btn-icon" onClick={handleCreate}><VscCheck size={13} /></button>
          <button className="btn-icon" onClick={() => { setCreating(false); setNewName(''); }}><VscClose size={13} /></button>
        </div>
      ) : canEdit && (
        <button className="sp-nav-item" style={{ color: 'var(--text-muted)', gap: 6 }}
          onClick={() => setCreating(true)}>
          <VscAdd size={13} /> New Diagram
        </button>
      )}

      {diagrams.length === 0 && !creating && (
        <div className="side-panel-empty">
          {canEdit ? 'Click "New Diagram" to start.' : 'No diagrams yet.'}
        </div>
      )}
    </div>
  );
}

export default PlanningSidebar;
